import { useState, useEffect, useRef, useMemo } from 'react'
import type { NavFn } from '../pages/SchedulerPage'
import { apiClient, useAuthStore } from '@indipia/shared'
import type { AssignedMember } from '@indipia/shared-features'
import { useHolidays } from '../hooks/useHolidays'
import { ErrorBanner } from '../components/AsyncState'
import { Button, Select, Segmented, EmptyState } from '@indipia/ui-core'
import { useNavigate } from 'react-router'
import type { TodoItem } from '@indipia/shared'
import { useTodayTodos } from '../hooks/useTaskSignals'
import { useSchedulerHost } from '../context/SchedulerHostContext'
import { TodayTodoButton } from '../components/SignalControls'
import { SignalTodoPanel } from '../components/SignalTodoPanel'
import { useSignalAction } from '../hooks/useSignalAction'

type Props = {
  navigate: NavFn
  inactiveSiteIds?: Set<number>
  /** 오늘 할일에 보일 현장(siteId) — 우측 목록에서 켜진 현장만. null·생략이면 거르지 않는다 */
  todoSiteIds?: Set<number> | null
  onSitesLoaded?: (sites: SiteInfo[]) => void
  jumpTo?: { startDate: string; endDate: string } | null
  onJumpDone?: () => void
  onMonthChange?: (year: number, month: number) => void
}

/* ── Public types ──────────────────────────────────────── */

export interface SiteInfo {
  scheduleId:     number
  siteId:         number
  siteName:       string
  customerName:   string
  contact:        string
  startDate:      string
  endDate:        string
  scheduleStatus: string
  /** 현장 종료(거래완료)로 완료된 스케줄이면 'Y' — 같은 DONE 이라도 공사완료는 'N'(스케줄 목록 API siteDoneYn) */
  siteDoneYn?:    string
  color:          string
  taskCount:      number
  progress:       number
  // 현장 담당자·관리자 (S-1②, 배정 순서) — 우측 현장 카드 표시용
  chargeMembers?:  AssignedMember[]
  managerMembers?: AssignedMember[]
}

/* ── API types ─────────────────────────────────────────── */

interface CalTask {
  scheduleId:    number
  siteName:      string
  taskId:        number
  workTypeCode:  string
  workTypeName:  string
  taskStartDate: string
  taskEndDate:   string
  /** 공종 진행 상태(INCOMPLETE/COMPLETE) — 반장 상태(partnerStatus)와 별개 */
  taskProgressStatus?: string | null
  progress:      number
  foremanName:   string | null
  scheduleMemo?: string | null
  consultMemos?: { content: string; createdAt?: string }[] | null
  orderDate?:    string | null
  deliveryDate?: string | null
}

interface ScheduleSummary {
  scheduleId:     number
  siteId:         number
  siteName:       string
  customerName:   string
  startDate:      string
  contact:        string
  endDate:        string
  scheduleStatus: string
  siteColor:      string | null
}

interface CalMilestone {
  scheduleId:    number
  milestoneId:   number
  milestoneType: string
  milestoneName: string
  milestoneDate: string
}

interface DisplayTask extends CalTask {
  startDay: number
  endDay:   number
  color:    string
  lane:     number
}

/* ── Site color palette ────────────────────────────────── */

type ViewMode = '일간' | '주간' | '월간'

function pad(n: number) { return String(n).padStart(2, '0') }
function daysInMonth(y: number, m: number) { return new Date(y, m, 0).getDate() }
function ds(y: number, m: number, d: number) { return `${y}-${pad(m)}-${pad(d)}` }
function getWeekDates(year: number, month: number, day: number) {
  const anchor = new Date(year, month - 1, day)
  const dow = anchor.getDay()
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(anchor)
    d.setDate(anchor.getDate() - dow + i)
    return { year: d.getFullYear(), month: d.getMonth() + 1, day: d.getDate(), dow: i }
  })
}

const SITE_PALETTE = [
  'var(--gt-primary)', '#1471A8', '#15803D', '#B91C1C', '#6D28D9',
  '#0891B2', '#9D174D', '#92400E', '#C4A035', '#1D4ED8',
]

function buildSiteColorMap(tasks: CalTask[], siteColors?: Map<number, string | null>): Map<number, string> {
  const map = new Map<number, string>()
  let i = 0
  for (const t of tasks) {
    if (!map.has(t.scheduleId)) {
      const fixed = siteColors?.get(t.scheduleId)
      if (fixed) {
        map.set(t.scheduleId, fixed)
      } else {
        map.set(t.scheduleId, SITE_PALETTE[i % SITE_PALETTE.length])
        i++
      }
    }
  }
  return map
}

/* ── Calendar helpers ──────────────────────────────────── */

function buildWeeks(year: number, month: number): (number | null)[][] {
  const firstDow = new Date(year, month - 1, 1).getDay()
  const lastDay  = new Date(year, month, 0).getDate()
  const cells: (number | null)[] = [
    ...Array(firstDow).fill(null),
    ...Array.from({ length: lastDay }, (_, i) => i + 1),
  ]
  while (cells.length % 7 !== 0) cells.push(null)
  const weeks: (number | null)[][] = []
  for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7))
  return weeks
}

/** Build display tasks grouped by site, each site gets its own lane block. */
function buildDisplayTasks(
  tasks: CalTask[],
  year: number,
  month: number,
  inactiveSiteIds: Set<number>,
  siteColorMap: Map<number, string>,
): DisplayTask[] {
  const lastDay = new Date(year, month, 0).getDate()

  // Clip to month and group by site, preserving task-list order for color stability
  const siteOrder: number[] = []
  const siteMap = new Map<number, (CalTask & { startDay: number; endDay: number })[]>()

  for (const t of tasks) {
    if (inactiveSiteIds.has(t.scheduleId)) continue

    const [sy, sm] = t.taskStartDate.split('-').map(Number)
    const [ey, em] = t.taskEndDate.split('-').map(Number)
    if (ey < year || (ey === year && em < month)) continue
    if (sy > year || (sy === year && sm > month)) continue

    const startDay = (sy < year || (sy === year && sm < month)) ? 1 : +t.taskStartDate.split('-')[2]
    const endDay   = (ey > year || (ey === year && em > month)) ? lastDay : +t.taskEndDate.split('-')[2]

    if (!siteMap.has(t.scheduleId)) {
      siteOrder.push(t.scheduleId)
      siteMap.set(t.scheduleId, [])
    }
    siteMap.get(t.scheduleId)!.push({ ...t, startDay, endDay })
  }

  // Sort sites by earliest start day in the month
  siteOrder.sort((a, b) => {
    const aMin = Math.min(...(siteMap.get(a) ?? []).map(t => t.startDay))
    const bMin = Math.min(...(siteMap.get(b) ?? []).map(t => t.startDay))
    return aMin - bMin
  })

  const result: DisplayTask[] = []
  let laneOffset = 0

  for (const scheduleId of siteOrder) {
    const siteTasks = siteMap.get(scheduleId) ?? []
    const color = siteColorMap.get(scheduleId) ?? 'var(--gt-primary)'

    // Sort within site: earlier start first, longer span first
    siteTasks.sort((a, b) =>
      a.startDay - b.startDay || (b.endDay - b.startDay) - (a.endDay - a.startDay)
    )

    // Greedy lane assignment within this site
    const localLaneEnds: number[] = []
    let siteMaxLanes = 0

    for (const task of siteTasks) {
      let lane = 0
      while (localLaneEnds[lane] !== undefined && localLaneEnds[lane] >= task.startDay) lane++
      localLaneEnds[lane] = task.endDay
      siteMaxLanes = Math.max(siteMaxLanes, lane + 1)
      result.push({ ...task, color, lane: laneOffset + lane })
    }

    laneOffset += siteMaxLanes
  }

  return result
}

/* ── Constants ─────────────────────────────────────────── */

const DN        = ['일','월','화','수','목','금','토']
const MAX_LANES = 4
const LANE_H    = 17
const LANE_GAP  = 2

/* ── Main ──────────────────────────────────────────────── */

export function CalendarMultiView({
  navigate,
  inactiveSiteIds = new Set(),
  todoSiteIds = null,
  onSitesLoaded = () => {},
  jumpTo,
  onJumpDone,
  onMonthChange,
}: Props) {
  const now = new Date()
  const todayStr = ds(now.getFullYear(), now.getMonth() + 1, now.getDate())
  const [year,     setYear]     = useState(now.getFullYear())
  const [month,    setMonth]    = useState(now.getMonth() + 1)
  const [selDay,   setSelDay]   = useState(now.getDate())
  const [viewMode, setViewMode] = useState<ViewMode>('월간')
  // 공휴일(BE `/schedule/holidays`) — 연말/연초 이동까지 감안해 전후 1년 조회
  const holidays = useHolidays(`${year - 1}-01-01`, `${year + 1}-12-31`)

  // 현재 보고 있는 연/월을 부모(우측 현장 패널의 "해당 달" 필터용)에 보고
  useEffect(() => { onMonthChange?.(year, month) }, [year, month, onMonthChange])
  // goPrev/goNext가 같은 렌더 사이클에서 연속 호출돼도(빠른 연타) 오래된 클로저 값이 아니라
  // 항상 최신 값을 기준으로 연/월을 굴리도록 ref로 미러링
  const yearRef  = useRef(year)
  const monthRef = useRef(month)
  const selDayRef = useRef(selDay)
  useEffect(() => { yearRef.current = year; monthRef.current = month; selDayRef.current = selDay }, [year, month, selDay])
  const [weekSelDate,      setWeekSelDate]      = useState<string | null>(null)

  const [rawTasks,    setRawTasks]    = useState<CalTask[]>([])
  const [milestones,  setMilestones]  = useState<CalMilestone[]>([])
  const [schedInfoMap, setSchedInfoMap] = useState<Map<number, { siteName: string; endDate: string }>>(new Map())
  const [scheduleColorMap, setScheduleColorMap] = useState<Map<number, string | null>>(new Map())
  // true로 시작 — false면 최초 마운트 시 fetch effect가 실행되기 전 한 프레임 동안
  // loading=false && hasAny=false로 렌더되어 "스케줄이 없어요" 빈 상태가 잘못 잠깐 보인다.
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)
  const [reloadTick, setReloadTick] = useState(0)
  const [filterForeman,   setFilterForeman]   = useState('')
  const [filterWorkType,  setFilterWorkType]  = useState('')
  const [daySelected,     setDaySelected]     = useState(false)

  const user = useAuthStore(s => s.user)
  // [오늘 할일 N] — 호출자 담당 현장 전체의 오늘 할일(빨강·노랑). 서버 신호 조회가 실패하면 오류 표시(폴백 없음).
  // 우측 목록에서 켜진 현장(todoSiteIds)의 할일만 남기고 건수도 그 기준으로 다시 센다(처리됨은 서버가 이미 빼고 내려준다).
  const todosQ = useTodayTodos()
  const todosData = useMemo(() => {
    const d = todosQ.data
    if (!d || !todoSiteIds) return d
    const items = d.items.filter(it => todoSiteIds.has(it.siteId))
    return {
      ...d,
      items,
      summary: { ...d.summary, pendingCount: items.filter(it => it.level === 'r').length, todayCount: items.filter(it => it.level !== 'r').length },
    }
  }, [todosQ.data, todoSiteIds])
  const [todoPanel, setTodoPanel] = useState<{ anchor: { x: number; y: number } } | null>(null)
  const schedulerHost = useSchedulerHost()
  const routerNavigate = useNavigate()
  const todoCount = todosData ? todosData.summary.pendingCount + todosData.summary.todayCount : 0
  // 처리 버튼 — 서버 주소(webPath)로 간다. 간트 주소는 같은 화면(인디플래너) 안 이동, 그 밖은 호스트 이동(useSignalAction)
  const todoAction = useSignalAction((_link, path) => { routerNavigate(path); return true })
  const handleTodoAct = (item: TodoItem) => { setTodoPanel(null); todoAction.act(item) }
  // 매 effect 실행(마운트 포함)마다 고유 id를 발급 — 같은 연/월/뷰모드라도(예: React
  // StrictMode의 마운트→클린업→재마운트 이중 실행) 서로 다른 실행으로 구분하기 위함.
  // 이전엔 "연-월-뷰모드" 문자열로만 최신 여부를 판단했는데, StrictMode 이중 실행은 이
  // 문자열이 완전히 같아서 먼저 abort된 실행의 .finally()가 setLoading(false)를 잘못
  // 호출해 실제 데이터가 오기 전 "이 달에는 스케줄이 없어요"가 잠깐 보이는 원인이었다.
  const runIdRef = useRef(0)
  useEffect(() => {
    // SchedulerPage의 현장 목록(allSites) fetch와 동일하게 compId 준비 전에는 조회를 미룬다.
    if (!user?.compId) return

    const myRunId = ++runIdRef.current
    const lastDay = daysInMonth(year, month)
    const fromDate = `${year}-${pad(month)}-01`
    const toDate   = `${year}-${pad(month)}-${pad(lastDay)}`
    const controller = new AbortController()
    setLoading(true)
    setLoadError(false)

    const headers = { 'X-Comp-Id': String(user.compId) }

    Promise.all([
      apiClient.get<{ data: { tasks: CalTask[]; milestones: CalMilestone[] } }>(
        '/schedule/tasks/calendar',
        { params: { fromDate, toDate }, headers, signal: controller.signal }
      ),
      apiClient.get<{ data: { schedules: ScheduleSummary[] } }>(
        '/schedule/schedules',
        { params: { pageSize: 100 }, headers, signal: controller.signal }
      ),
    ])
      .then(async ([calRes, schRes]) => {
        if (runIdRef.current !== myRunId) return
        const schedules  = schRes.data.data?.schedules  ?? []
        // 캘린더 태스크/마일스톤은 현재 사용자의 현장 목록(schedules)에 실제로 존재하는
        // scheduleId만 신뢰한다 — 삭제되었거나 다른 회사 소속인 고아(orphan) 태스크가
        // 섞여 내려와도(백엔드 응답 이슈) 현장이 0개인 신규 사장님 계정에 공종이
        // 잘못 표시되지 않도록 방어한다.
        // 인디플래너 홈 캘린더는 준비중·시공중 현장만 노출한다 — 시공완료(DONE) 현장은
        // 현장관리·전송이력 등 별도 화면에서만 조회한다.
        const validScheduleIds = new Set(
          schedules.filter(s => s.scheduleStatus !== 'DONE' && s.scheduleStatus !== 'COMPLETED').map(s => s.scheduleId)
        )
        const tasks      = (calRes.data.data?.tasks      ?? []).filter(t => validScheduleIds.has(t.scheduleId))
        const rawMiles   = (calRes.data.data?.milestones ?? []).filter(m => validScheduleIds.has(m.scheduleId))

        // Enrich tasks with scheduleMemo + consultMemos from schedule detail API, and
        // 자재 발주일/입고예정일(orderDate/deliveryDate)은 마일스톤처럼 캘린더에 함께 표시하기
        // 위해 스케줄당 한 번씩 /orders 엔드포인트에서 taskId 기준으로 미리 불러온다.
        type TaskMemo = { scheduleMemo?: string | null; consultMemos?: { content: string; createdAt?: string }[] | null }
        const memoMap  = new Map<number, TaskMemo>()
        const orderMap = new Map<number, { orderDate: string | null; deliveryDate: string | null }>()
        const uniqueIds = [...new Set(tasks.map(t => t.scheduleId))]
        await Promise.all(uniqueIds.map(sid => Promise.all([
          apiClient.get<{ data: { tasks: (TaskMemo & { taskId: number })[] } }>(
            `/schedule/schedules/${sid}`, { headers, signal: controller.signal }
          ).then(r => {
            for (const t of r.data.data?.tasks ?? []) {
              memoMap.set(t.taskId, { scheduleMemo: t.scheduleMemo, consultMemos: t.consultMemos })
            }
          }).catch(() => {}),
          apiClient.get<{ data: { orders: { taskId: number; orderDate: string | null; deliveryDate: string | null }[] } }>(
            `/schedule/schedules/${sid}/orders`, { headers, signal: controller.signal }
          ).then(r => {
            for (const o of r.data.data?.orders ?? []) {
              orderMap.set(o.taskId, { orderDate: o.orderDate, deliveryDate: o.deliveryDate })
            }
          }).catch(() => {}),
        ])))
        const enrichedTasks = tasks.map(t => ({ ...t, ...memoMap.get(t.taskId), ...orderMap.get(t.taskId) }))

        if (runIdRef.current !== myRunId) return
        setRawTasks(enrichedTasks)
        setMilestones(rawMiles)
        setSchedInfoMap(new Map(schedules.map(s => [s.scheduleId, { siteName: s.siteName, endDate: s.endDate }])))
        setScheduleColorMap(new Map(schedules.map(s => [s.scheduleId, s.siteColor])))

        // Build schedule-level meta map (customerName, contact, endDate, scheduleStatus)
        const scheduleMetaMap = new Map<number, ScheduleSummary>(
          schedules.map(s => [s.scheduleId, s])
        )

        // Build SiteInfo only from tasks in this month (calendar-scoped)
        const colorMap    = buildSiteColorMap(tasks, new Map(schedules.map(s => [s.scheduleId, s.siteColor])))
        const siteInfoMap = new Map<number, SiteInfo>()
        const progSumMap  = new Map<number, number>()
        for (const t of tasks) {
          if (!siteInfoMap.has(t.scheduleId)) {
            const meta = scheduleMetaMap.get(t.scheduleId)
            siteInfoMap.set(t.scheduleId, {
              scheduleId:     t.scheduleId,
              siteId:         meta?.siteId ?? 0,
              siteName:       meta?.siteName       ?? t.siteName ?? '',
              customerName:   meta?.customerName   ?? '',
              contact:        meta?.contact        ?? '',
              startDate:      meta?.startDate      ?? '',
              endDate:        meta?.endDate        ?? '',
              scheduleStatus: meta?.scheduleStatus ?? '',
              color:          colorMap.get(t.scheduleId) ?? 'var(--gt-primary)',
              taskCount:      0,
              progress:       0,
            })
            progSumMap.set(t.scheduleId, 0)
          }
          siteInfoMap.get(t.scheduleId)!.taskCount++
          progSumMap.set(t.scheduleId, (progSumMap.get(t.scheduleId) ?? 0) + (t.progress ?? 0))
        }
        for (const [id, info] of siteInfoMap) {
          info.progress = info.taskCount > 0
            ? Math.round((progSumMap.get(id) ?? 0) / info.taskCount)
            : 0
        }
        onSitesLoaded(Array.from(siteInfoMap.values()))
      })
      .catch(err => {
        if (runIdRef.current !== myRunId) return
        if (err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError') return
        console.error('[CalendarMultiView] fetch error:', err)
        setRawTasks([])
        setMilestones([])
        onSitesLoaded([])
        setLoadError(true)
      })
      .finally(() => {
        if (runIdRef.current === myRunId) setLoading(false)
      })
    return () => controller.abort()
  // onSitesLoaded is stable (useCallback in parent)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year, month, viewMode, user?.compId, reloadTick])

  // 주간 뷰로 들어오면 오늘 날짜를 기본 선택(바로 오늘 작업이 보이도록), 다른 뷰로
  // 나가면 선택 초기화
  useEffect(() => { setWeekSelDate(viewMode === '주간' ? todayStr : null) }, [viewMode, todayStr])

  useEffect(() => {
    if (!jumpTo) return
    const start = new Date(jumpTo.startDate)
    const end = new Date(jumpTo.endDate)
    const startYM = start.getFullYear() * 12 + start.getMonth()
    const endYM = end.getFullYear() * 12 + end.getMonth()
    const curYM = year * 12 + (month - 1)
    // 현재 보고 있는 달이 이미 공사 기간(시작~종료) 안에 포함되면 이동하지 않음
    if (curYM < startYM || curYM > endYM) {
      setYear(end.getFullYear())
      setMonth(end.getMonth() + 1)
      setSelDay(1)
    }
    onJumpDone?.()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jumpTo])

  // Compute derived data on every render (cheap – no network)

  // Filter options from raw task list
  const foremanOptions = [...new Set(rawTasks.map(t => t.foremanName).filter((n): n is string => !!n))]
  const workTypeOptions = [...new Map(rawTasks.map(t => [t.workTypeCode, t.workTypeName])).entries()]

  // Apply filters before building display tasks — 비활성화된 현장은 일간/주간/월간 모두
  // 동일하게 반영되어야 하므로 여기서 한 번에 걸러낸다(전에는 월간만 buildDisplayTasks
  // 내부에서 걸러내고, 일간·주간은 filteredTasks를 그대로 써서 반응하지 않았다).
  const filteredTasks = rawTasks.filter(t => {
    if (inactiveSiteIds.has(t.scheduleId)) return false
    if (filterForeman  && t.foremanName  !== filterForeman)  return false
    if (filterWorkType && t.workTypeCode !== filterWorkType) return false
    return true
  })

  const siteColorMap   = buildSiteColorMap(rawTasks, scheduleColorMap)   // color from full list for stability
  const displayTasks   = buildDisplayTasks(filteredTasks, year, month, inactiveSiteIds, siteColorMap)
  const weeks          = buildWeeks(year, month)
  const hasAny         = rawTasks.length > 0

  // Milestones filtered by inactive sites
  const filteredMilestones = milestones.filter(m => !inactiveSiteIds.has(m.scheduleId))

  const siteNameMap = new Map<number, string>(
    [...schedInfoMap.entries()].map(([id, { siteName }]) => [id, siteName])
  )
  const todayMs = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime()



  const goPrev = () => {
    if (viewMode === '일간') {
      const d = new Date(yearRef.current, monthRef.current - 1, selDayRef.current - 1)
      yearRef.current = d.getFullYear(); monthRef.current = d.getMonth() + 1; selDayRef.current = d.getDate()
      setYear(yearRef.current); setMonth(monthRef.current); setSelDay(selDayRef.current)
    } else if (viewMode === '주간') {
      const d = new Date(yearRef.current, monthRef.current - 1, selDayRef.current - 7)
      yearRef.current = d.getFullYear(); monthRef.current = d.getMonth() + 1; selDayRef.current = d.getDate()
      setYear(yearRef.current); setMonth(monthRef.current); setSelDay(selDayRef.current)
    } else if (viewMode === '월간') {
      selDayRef.current = 1
      setSelDay(1); setDaySelected(false)
      if (monthRef.current === 1) { yearRef.current -= 1; monthRef.current = 12 } else { monthRef.current -= 1 }
      setYear(yearRef.current); setMonth(monthRef.current)
    } else {
      yearRef.current -= 1
      setYear(yearRef.current)
    }
  }
  const goNext = () => {
    if (viewMode === '일간') {
      const d = new Date(yearRef.current, monthRef.current - 1, selDayRef.current + 1)
      yearRef.current = d.getFullYear(); monthRef.current = d.getMonth() + 1; selDayRef.current = d.getDate()
      setYear(yearRef.current); setMonth(monthRef.current); setSelDay(selDayRef.current)
    } else if (viewMode === '주간') {
      const d = new Date(yearRef.current, monthRef.current - 1, selDayRef.current + 7)
      yearRef.current = d.getFullYear(); monthRef.current = d.getMonth() + 1; selDayRef.current = d.getDate()
      setYear(yearRef.current); setMonth(monthRef.current); setSelDay(selDayRef.current)
    } else if (viewMode === '월간') {
      selDayRef.current = 1
      setSelDay(1); setDaySelected(false)
      if (monthRef.current === 12) { yearRef.current += 1; monthRef.current = 1 } else { monthRef.current += 1 }
      setYear(yearRef.current); setMonth(monthRef.current)
    } else {
      yearRef.current += 1
      setYear(yearRef.current)
    }
  }
  const getTitle = () => {
    if (viewMode === '일간') return `${year}년 ${month}월 ${selDay}일`
    if (viewMode === '주간') {
      const wk = getWeekDates(year, month, selDay)
      const f = wk[0], l = wk[6]
      if (f.month === l.month) return `${f.year}년 ${f.month}월 ${f.day}일 ~ ${l.day}일`
      return `${f.year}년 ${f.month}월 ${f.day}일 ~ ${l.month}월 ${l.day}일`
    }
    return `${year}년 ${month}월`
  }

  const navBtn = { border: 'var(--border-width-hairline) solid var(--shell-border)', borderRadius: 12, background: 'var(--shell-surface)', cursor: 'pointer', padding: '4px 9px', fontSize: 12, color: 'var(--shell-on-surface)', fontFamily: 'inherit' } as const

  const selDayStr     = ds(year, month, selDay)
  const selMilestones = filteredMilestones.filter(m => m.milestoneDate.slice(0, 10) === selDayStr)
  const selOrders     = filteredTasks.filter(t => t.orderDate?.slice(0, 10) === selDayStr)
  const selDeliveries = filteredTasks.filter(t => t.deliveryDate?.slice(0, 10) === selDayStr)

  const selDow        = DN[new Date(year, month - 1, selDay).getDay()]
  const panelTasks    = filteredTasks.filter(t =>
    t.taskStartDate.slice(0, 10) <= selDayStr &&
    t.taskEndDate.slice(0, 10)   >= selDayStr
  )

  const renderDay = () => {
    const dateStr  = ds(year, month, selDay)
    const dayTasks = filteredTasks.filter(t => t.taskStartDate.slice(0, 10) <= dateStr && t.taskEndDate.slice(0, 10) >= dateStr)
    const dayMiles = filteredMilestones.filter(m => m.milestoneDate.slice(0, 10) === dateStr)
    const dayOrders     = filteredTasks.filter(t => t.orderDate?.slice(0, 10) === dateStr)
    const dayDeliveries = filteredTasks.filter(t => t.deliveryDate?.slice(0, 10) === dateStr)
    if (dayTasks.length === 0 && dayMiles.length === 0 && dayOrders.length === 0 && dayDeliveries.length === 0) return (
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <EmptyState title="이 날짜에 공종이 없습니다" />
      </div>
    )
    return (
      <div style={{ flex: 1, overflowY: 'auto', padding: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
        {dayMiles.map(m => {
          const color    = siteColorMap.get(m.scheduleId) ?? 'var(--gt-primary)'
          const siteName = siteNameMap.get(m.scheduleId) ?? ''
          const [my, mm, md] = m.milestoneDate.slice(0, 10).split('-').map(Number)
          const daysUntil = Math.round((new Date(my, mm - 1, md).getTime() - todayMs) / 86400000)
          const dxLabel   = daysUntil >= 0 ? `D-${daysUntil}` : `D+${Math.abs(daysUntil)}`
          return (
            <div key={`ms-${m.milestoneId}`} style={{ background: 'var(--gt-subtle)', border: `var(--border-width-hairline) solid color-mix(in srgb, ${color} 20%, transparent)`, borderLeft: `3px solid ${color}`, borderRadius: 12, padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 0 }}>
              <span style={{ fontSize: 13, color, flexShrink: 0, marginRight: 8 }}>◆</span>
              {siteName && <><span style={{ fontSize: 'var(--type-caption-size)', color: 'var(--shell-on-surface-2)', flexShrink: 0, maxWidth: 100, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{siteName}</span>
              <span style={{ fontSize: 'var(--type-caption-size)', color: 'var(--shell-hint)', flexShrink: 0, margin: '0 6px' }}>/</span></>}
              <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--shell-on-surface)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.milestoneName}</span>
              <span style={{ fontSize: 'var(--type-caption-size)', color: 'var(--shell-hint)', flexShrink: 0, margin: '0 6px' }}>/</span>
              <span style={{ fontSize: 'var(--type-caption-size)', fontWeight: 700, color, flexShrink: 0 }}>{dxLabel}</span>
            </div>
          )
        })}
        {dayOrders.map(t => (
          <div key={`ord-${t.taskId}`} style={{ background: 'var(--gt-subtle)', border: 'var(--border-width-hairline) solid color-mix(in srgb, var(--gt-primary) 25%, var(--shell-surface))', borderLeft: '3px solid var(--gt-primary)', borderRadius: 12, padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 0 }}>
            <span style={{ fontSize: 13, color: 'var(--gt-primary)', flexShrink: 0, marginRight: 8 }}>◆</span>
            <span style={{ fontSize: 'var(--type-caption-size)', color: 'var(--shell-on-surface-2)', flexShrink: 0, maxWidth: 100, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.siteName}</span>
            <span style={{ fontSize: 'var(--type-caption-size)', color: 'var(--shell-hint)', flexShrink: 0, margin: '0 6px' }}>/</span>
            <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--shell-on-surface)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.workTypeName} 발주</span>
          </div>
        ))}
        {dayDeliveries.map(t => (
          <div key={`del-${t.taskId}`} style={{ background: 'var(--shell-bg)', border: 'var(--border-width-hairline) solid var(--shell-border)', borderLeft: '3px solid var(--shell-on-surface)', borderRadius: 12, padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 0 }}>
            <span style={{ fontSize: 13, color: 'var(--shell-on-surface)', flexShrink: 0, marginRight: 8 }}>●</span>
            <span style={{ fontSize: 'var(--type-caption-size)', color: 'var(--shell-on-surface-2)', flexShrink: 0, maxWidth: 100, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.siteName}</span>
            <span style={{ fontSize: 'var(--type-caption-size)', color: 'var(--shell-hint)', flexShrink: 0, margin: '0 6px' }}>/</span>
            <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--shell-on-surface)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.workTypeName} 입고</span>
          </div>
        ))}
        {dayTasks.map(t => {
          const color = siteColorMap.get(t.scheduleId) ?? 'var(--gt-primary)'
          const start = new Date(t.taskStartDate.slice(0, 10))
          const end   = new Date(t.taskEndDate.slice(0, 10))
          const total   = Math.round((end.getTime() - start.getTime()) / 86400000) + 1
          const elapsed = Math.round((new Date(year, month - 1, selDay).getTime() - start.getTime()) / 86400000) + 1
          return (
            <div key={t.taskId} style={{ background: 'var(--shell-surface)', border: 'var(--border-width-hairline) solid var(--shell-border)', borderLeft: `3px solid ${color}`, borderRadius: 12, padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 0 }}>
              <span style={{ fontSize: 'var(--type-caption-size)', color: 'var(--shell-on-surface-2)', flexShrink: 0, width: 80, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.siteName || '—'}</span>
              <span style={{ width: 1, height: 12, background: 'var(--shell-border)', flexShrink: 0, margin: '0 8px' }} />
              <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--shell-on-surface)', flexShrink: 0, width: 96, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.workTypeName}</span>
              <span style={{ width: 1, height: 12, background: 'var(--shell-border)', flexShrink: 0, margin: '0 8px' }} />
              <span style={{ fontSize: 'var(--type-caption-size)', color: t.foremanName ? 'var(--shell-on-surface)' : 'var(--shell-hint)', flexShrink: 0, width: 64, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.foremanName ?? '미배정'}</span>
              <span style={{ fontSize: 'var(--type-caption-size)', color: color, fontWeight: 700, flexShrink: 0, marginLeft: 'auto' }}>{elapsed}/{total}일</span>
            </div>
          )
        })}
      </div>
    )
  }

  const renderWeek = () => {
    const weekDates = getWeekDates(year, month, selDay)
    return (
      <div style={{ flex: 1, overflowY: 'auto', background: 'var(--shell-bg)', padding: 12 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 6 }}>
          {weekDates.map(({ year: wy, month: wm, day: wd, dow }) => {
            const dateStr  = ds(wy, wm, wd)
            const dayTasks = filteredTasks.filter(t => t.taskStartDate.slice(0, 10) <= dateStr && t.taskEndDate.slice(0, 10) >= dateStr)
            const wkMiles  = filteredMilestones.filter(m => m.milestoneDate.slice(0, 10) === dateStr)
            const wkOrders     = filteredTasks.filter(t => t.orderDate?.slice(0, 10) === dateStr)
            const wkDeliveries = filteredTasks.filter(t => t.deliveryDate?.slice(0, 10) === dateStr)
            const isToday  = dateStr === todayStr
            const isSel    = dateStr === weekSelDate
            return (
              <div
                key={dateStr}
                role="button"
                tabIndex={0}
                onClick={() => setWeekSelDate(isSel ? null : dateStr)}
                onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setWeekSelDate(isSel ? null : dateStr) } }}
                style={{ background: isSel ? 'var(--gt-subtle)' : isToday ? 'var(--gt-subtle)' : 'var(--shell-surface)', border: `var(--border-width-hairline) solid ${isSel ? 'var(--gt-primary)' : isToday ? 'var(--shell-border)' : 'var(--shell-border)'}`, borderRadius: 12, minHeight: 120, overflow: 'hidden', cursor: 'pointer' }}
              >
                <div style={{ padding: '6px 8px', borderBottom: `var(--border-width-hairline) solid ${isSel ? 'color-mix(in srgb, var(--gt-primary) 25%, var(--shell-surface))' : 'var(--shell-border)'}`, textAlign: 'center' }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: (dow === 0 || holidays.has(dateStr)) ? 'var(--status-danger-600)' : dow === 6 ? 'var(--status-info-600)' : 'var(--shell-on-surface-2)' }}>{DN[dow]}</div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: isSel ? 'var(--gt-primary)' : isToday ? 'var(--gt-primary)' : 'var(--shell-on-surface)', marginTop: 1 }}>{wd}</div>
                  {wm !== month && <div style={{ fontSize: 9, color: 'var(--shell-hint)' }}>{wm}월</div>}
                </div>
                <div style={{ padding: '4px', display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {dayTasks.slice(0, 5).map(t => {
                    const color = siteColorMap.get(t.scheduleId) ?? 'var(--gt-primary)'
                    return (
                      <div key={t.taskId} style={{ background: color, borderRadius: 4, padding: '2px 5px', fontSize: 10, fontWeight: 600, color: 'var(--gt-on-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {t.workTypeName}
                      </div>
                    )
                  })}
                  {dayTasks.length > 5 && <div style={{ fontSize: 9, color: 'var(--shell-on-surface-2)', padding: '0 4px' }}>+{dayTasks.length - 5}개</div>}
                  {wkMiles.map(m => {
                    const mc = siteColorMap.get(m.scheduleId) ?? 'var(--gt-primary)'
                    return (
                      <div key={m.milestoneId} style={{ display: 'flex', alignItems: 'center', gap: 2, padding: '1px 5px' }}>
                        <span style={{ fontSize: 8, color: mc }}>◆</span>
                        <span style={{ fontSize: 9, fontWeight: 700, color: mc, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.milestoneName}</span>
                      </div>
                    )
                  })}
                  {wkOrders.map(t => (
                    <div key={`ord-${t.taskId}`} style={{ display: 'flex', alignItems: 'center', gap: 2, padding: '1px 5px' }}>
                      <span style={{ fontSize: 8, color: 'var(--gt-primary)' }}>◆</span>
                      <span style={{ fontSize: 9, fontWeight: 700, color: 'var(--gt-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.workTypeName} 발주</span>
                    </div>
                  ))}
                  {wkDeliveries.map(t => (
                    <div key={`del-${t.taskId}`} style={{ display: 'flex', alignItems: 'center', gap: 2, padding: '1px 5px' }}>
                      <span style={{ fontSize: 8, color: 'var(--shell-on-surface)' }}>●</span>
                      <span style={{ fontSize: 9, fontWeight: 700, color: 'var(--shell-on-surface)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.workTypeName} 입고</span>
                    </div>
                  ))}
                </div>
              </div>
            )
          })}
        </div>
      </div>
    )
  }

  const renderWeekDetail = () => {
    if (!weekSelDate) return null
    const [wy, wm, wd] = weekSelDate.split('-').map(Number)
    const selDt    = new Date(wy, wm - 1, wd)
    const dayTasks = filteredTasks.filter(t =>
      t.taskStartDate.slice(0, 10) <= weekSelDate &&
      t.taskEndDate.slice(0, 10)   >= weekSelDate
    )
    const dayMiles = filteredMilestones.filter(m => m.milestoneDate.slice(0, 10) === weekSelDate)
    const dayOrders     = filteredTasks.filter(t => t.orderDate?.slice(0, 10) === weekSelDate)
    const dayDeliveries = filteredTasks.filter(t => t.deliveryDate?.slice(0, 10) === weekSelDate)
    return (
      <div style={{ borderTop: 'var(--border-width-hairline) solid var(--shell-border)', background: 'var(--shell-surface)', flexShrink: 0, maxHeight: '45%', display: 'flex', flexDirection: 'column' }}>
        {/* header */}
        <div style={{ padding: '10px 14px 6px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--shell-on-surface)', letterSpacing: '-.3px' }}>
            {wm}월 {wd}일 · {dayTasks.length + dayMiles.length + dayOrders.length + dayDeliveries.length}개 작업
          </span>
          <button
            onClick={() => setWeekSelDate(null)}
            aria-label="닫기"
            style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: 18, color: 'var(--shell-hint)', lineHeight: 1, padding: 0, fontFamily: 'inherit' }}
          >×</button>
        </div>
        {/* list */}
        <div style={{ overflowY: 'auto', padding: '0 14px 14px', display: 'flex', flexDirection: 'column', gap: 8 }}>
          {dayTasks.length === 0 && dayMiles.length === 0 && dayOrders.length === 0 && dayDeliveries.length === 0 ? (
            <div style={{ padding: '16px 0', textAlign: 'center', fontSize: 12, color: 'var(--shell-hint)' }}>이 날짜에 공종이 없습니다</div>
          ) : <>
          {dayMiles.map(m => {
            const color    = siteColorMap.get(m.scheduleId) ?? 'var(--gt-primary)'
            const siteName = siteNameMap.get(m.scheduleId) ?? ''
            const [my, mm, md] = m.milestoneDate.slice(0, 10).split('-').map(Number)
            const daysUntil = Math.round((new Date(my, mm - 1, md).getTime() - todayMs) / 86400000)
            const dxLabel   = daysUntil >= 0 ? `D-${daysUntil}` : `D+${Math.abs(daysUntil)}`
            return (
              <div key={`ms-${m.milestoneId}`} style={{ background: 'var(--gt-subtle)', borderRadius: 12, border: `var(--border-width-hairline) solid color-mix(in srgb, ${color} 20%, transparent)`, borderLeft: `3.5px solid ${color}`, padding: '10px 14px', display: 'flex', flexDirection: 'column', gap: 4 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
                  <span style={{ fontSize: 13, color, flexShrink: 0, marginRight: 8 }}>◆</span>
                  {siteName && <><span style={{ fontSize: 'var(--type-caption-size)', color: 'var(--shell-on-surface-2)', flexShrink: 0, maxWidth: 100, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{siteName}</span>
                  <span style={{ fontSize: 'var(--type-caption-size)', color: 'var(--shell-hint)', flexShrink: 0, margin: '0 6px' }}>/</span></>}
                  <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--shell-on-surface)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.milestoneName}</span>
                  <span style={{ fontSize: 'var(--type-caption-size)', color: 'var(--shell-hint)', flexShrink: 0, margin: '0 6px' }}>/</span>
                  <span style={{ fontSize: 12, fontWeight: 700, color, flexShrink: 0 }}>{dxLabel}</span>
                </div>
              </div>
            )
          })}
          {dayOrders.map(t => (
            <div key={`ord-${t.taskId}`} style={{ background: 'var(--gt-subtle)', borderRadius: 12, border: 'var(--border-width-hairline) solid color-mix(in srgb, var(--gt-primary) 25%, var(--shell-surface))', borderLeft: '3.5px solid var(--gt-primary)', padding: '10px 14px', display: 'flex', flexDirection: 'column', gap: 4 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
                <span style={{ fontSize: 13, color: 'var(--gt-primary)', flexShrink: 0, marginRight: 8 }}>◆</span>
                <span style={{ fontSize: 'var(--type-caption-size)', color: 'var(--shell-on-surface-2)', flexShrink: 0, maxWidth: 100, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.siteName}</span>
                <span style={{ fontSize: 'var(--type-caption-size)', color: 'var(--shell-hint)', flexShrink: 0, margin: '0 6px' }}>/</span>
                <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--shell-on-surface)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.workTypeName} 발주</span>
              </div>
            </div>
          ))}
          {dayDeliveries.map(t => (
            <div key={`del-${t.taskId}`} style={{ background: 'var(--shell-bg)', borderRadius: 12, border: 'var(--border-width-hairline) solid var(--shell-border)', borderLeft: '3.5px solid var(--shell-on-surface)', padding: '10px 14px', display: 'flex', flexDirection: 'column', gap: 4 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
                <span style={{ fontSize: 13, color: 'var(--shell-on-surface)', flexShrink: 0, marginRight: 8 }}>●</span>
                <span style={{ fontSize: 'var(--type-caption-size)', color: 'var(--shell-on-surface-2)', flexShrink: 0, maxWidth: 100, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.siteName}</span>
                <span style={{ fontSize: 'var(--type-caption-size)', color: 'var(--shell-hint)', flexShrink: 0, margin: '0 6px' }}>/</span>
                <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--shell-on-surface)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.workTypeName} 입고</span>
              </div>
            </div>
          ))}
          {dayTasks.map(t => {
            const color   = siteColorMap.get(t.scheduleId) ?? 'var(--gt-primary)'
            const start   = new Date(t.taskStartDate.slice(0, 10))
            const end     = new Date(t.taskEndDate.slice(0, 10))
            const total   = Math.round((end.getTime()   - start.getTime()) / 86400000) + 1
            const elapsed = Math.round((selDt.getTime() - start.getTime()) / 86400000) + 1
            return (
              <div key={t.taskId} style={{ background: 'var(--shell-bg)', borderRadius: 12, border: 'var(--border-width-hairline) solid var(--shell-border)', borderLeft: `3.5px solid ${color}`, padding: '10px 14px', display: 'flex', flexDirection: 'column', gap: 6 }}>
                {/* top row */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
                  <span style={{ fontSize: 'var(--type-caption-size)', color: 'var(--shell-on-surface-2)', flexShrink: 0, maxWidth: 80, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.siteName || '—'}</span>
                  <span style={{ width: 1, height: 11, background: 'var(--shell-border)', flexShrink: 0, margin: '0 7px' }} />
                  <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--shell-on-surface)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', letterSpacing: '-.2px' }}>{t.workTypeName}</span>
                  <span style={{ fontSize: 12, fontWeight: 700, color: color, flexShrink: 0, marginLeft: 8 }}>{elapsed}/{total}일</span>
                </div>
                {/* meta row */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 'var(--type-caption-size)', color: 'var(--shell-on-surface)' }}>
                  <svg aria-hidden="true" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="var(--shell-hint)" strokeWidth="2.2" strokeLinecap="round"><circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/></svg>
                  <span>{t.foremanName ?? '미배정'}</span>
                  <span style={{ width: 1, height: 10, background: 'var(--shell-border)', margin: '0 2px' }} />
                  <span style={{ color: 'var(--shell-on-surface-2)' }}>{t.taskStartDate.slice(0, 10)} ~ {t.taskEndDate.slice(0, 10)}</span>
                </div>
                {/* scheduleMemo */}
                {t.scheduleMemo && (
                  <div style={{ display: 'flex', gap: 6, fontSize: 'var(--type-caption-size)', color: 'var(--shell-on-surface)', background: 'var(--shell-surface)', borderRadius: 4, padding: '6px 9px', border: 'var(--border-width-hairline) solid var(--shell-border)' }}>
                    <svg aria-hidden="true" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="var(--shell-hint)" strokeWidth="2.2" strokeLinecap="round" style={{ flexShrink: 0, marginTop: 1 }}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>
                    <span style={{ lineHeight: 1.5 }}>{t.scheduleMemo}</span>
                  </div>
                )}
                {/* consultMemos */}
                {t.consultMemos && t.consultMemos.length > 0 && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {t.consultMemos.map((memo, i) => (
                      <div key={i} style={{ display: 'flex', gap: 6, fontSize: 'var(--type-caption-size)', color: 'var(--shell-on-surface)', background: 'var(--gt-subtle)', borderRadius: 4, padding: '6px 9px', border: 'var(--border-width-hairline) solid color-mix(in srgb, var(--gt-primary) 20%, var(--shell-surface))' }}>
                        <svg aria-hidden="true" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="var(--gt-primary)" strokeWidth="2.2" strokeLinecap="round" style={{ flexShrink: 0, marginTop: 1 }}><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
                        <span style={{ lineHeight: 1.5 }}>{memo.content}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )
          })}
          </>}
        </div>
      </div>
    )
  }

  return (
    <div className="flex h-full flex-col overflow-hidden">

      {/* Toolbar */}
      <div style={{ background: 'var(--shell-surface)', borderBottom: 'var(--border-width-hairline) solid var(--shell-border)', flexShrink: 0 }}>
        {/* Row 1: nav + tab + create */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px' }}>
          {/* Nav group */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <button style={navBtn} onClick={goPrev} aria-label={`이전 ${viewMode === '일간' ? '날' : viewMode === '주간' ? '주' : '달'}`}>‹</button>
            <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--shell-on-surface)', letterSpacing: '-.4px', minWidth: 120, textAlign: 'center' }}>
              {getTitle()}
            </span>
            <button style={navBtn} onClick={goNext} aria-label={`다음 ${viewMode === '일간' ? '날' : viewMode === '주간' ? '주' : '달'}`}>›</button>
          </div>
          <div style={{ flex: 1 }} />
          {/* View tab group */}
          <Segmented
            className="!shrink-0"
            options={(['일간', '주간', '월간'] as const).map(mode => ({ label: mode.slice(0, 1), value: mode }))}
            value={viewMode}
            onChange={v => {
              const mode = v as typeof viewMode
              setViewMode(mode)
              setDaySelected(false)
              // 일간/주간 탭을 누르면 (다른 달을 보고 있었더라도) 오늘 기준으로 이동
              if (mode === '일간' || mode === '주간') {
                setYear(now.getFullYear())
                setMonth(now.getMonth() + 1)
                setSelDay(now.getDate())
              }
            }}
          />
          {/* 오늘 할일 — 0건이면 숨김 */}
          <TodayTodoButton
            size="sm"
            count={todoCount}
            isError={todosQ.isError}
            isLoading={todosQ.isLoading}
            onOpen={anchor => setTodoPanel({ anchor })}
          />
          {/* 신규 스케줄 생성 */}
          <Button
            variant="primary"
            size="sm"
            className="!h-auto gap-[5px] !px-3.5 !py-[7px] text-[12px]"
            onClick={() => navigate('create-site', undefined, undefined, { year, month })}
          >
            <svg aria-hidden="true" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.8" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
            스케줄 생성
          </Button>
        </div>
        {/* Row 2: filters */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 16px 7px', borderTop: 'var(--border-width-hairline) solid var(--shell-border)', background: 'var(--shell-bg)' }}>
          <label htmlFor="cal-filter-foreman" style={{ fontSize: 'var(--type-caption-size)', fontWeight: 600, color: 'var(--shell-on-surface-2)', letterSpacing: '-.1px', flexShrink: 0, marginRight: 2 }}>시공반장</label>
          <Select
            id="cal-filter-foreman"
            value={filterForeman}
            onChange={e => setFilterForeman(e.target.value)}
            size="sm"
            className="field-auto"
            style={{ borderColor: filterForeman ? 'var(--gt-primary)' : undefined, color: filterForeman ? 'var(--gt-primary)' : undefined }}
          >
            <option value="">전체</option>
            {foremanOptions.map(name => (
              <option key={name} value={name}>{name}</option>
            ))}
          </Select>
          <label htmlFor="cal-filter-worktype" style={{ fontSize: 'var(--type-caption-size)', fontWeight: 600, color: 'var(--shell-on-surface-2)', letterSpacing: '-.1px', flexShrink: 0, marginLeft: 4, marginRight: 2 }}>공종</label>
          <Select
            id="cal-filter-worktype"
            value={filterWorkType}
            onChange={e => setFilterWorkType(e.target.value)}
            size="sm"
            className="field-auto"
            style={{ borderColor: filterWorkType ? 'var(--gt-primary)' : undefined, color: filterWorkType ? 'var(--gt-primary)' : undefined }}
          >
            <option value="">전체</option>
            {workTypeOptions.map(([code, name]) => (
              <option key={code} value={code}>{name}</option>
            ))}
          </Select>
        </div>
      </div>

      {/* 조회 실패 배너 — 빈 상태와 구분해서 표시 */}
      {!loading && loadError && (
        <div style={{ margin: '12px 14px 0' }}>
          <ErrorBanner message="일정 정보를 불러오지 못했습니다." onRetry={() => setReloadTick(k => k + 1)} />
        </div>
      )}

      {/* Empty-state banner */}
      {!loading && !loadError && !hasAny && (
        <div style={{ background: 'var(--gt-subtle)', border: 'var(--border-width-hairline) solid color-mix(in srgb, var(--gt-primary) 25%, var(--shell-surface))', borderRadius: 12, margin: '12px 14px 0', padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12 }}>
          <span aria-hidden="true" style={{ fontSize: 18 }}>📋</span>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--gt-primary)', marginBottom: 2 }}>이 달에는 스케줄이 없어요</div>
            <div style={{ fontSize: 'var(--type-caption-size)', color: 'var(--shell-on-surface-2)' }}>현장을 선택하고 견적서를 업로드하면 AI가 일정을 자동 생성합니다.</div>
          </div>
          <Button
            variant="primary"
            size="sm"
            className="!h-auto ml-auto !px-3.5 !py-[7px] text-[12px]"
            onClick={() => navigate('create-site')}
          >
            스케줄 생성
          </Button>
        </div>
      )}

      {todoPanel && (
        <SignalTodoPanel
          anchor={todoPanel.anchor}
          data={todosData}
          isLoading={todosQ.isLoading}
          isError={todosQ.isError}
          onRetry={() => { void todosQ.refetch() }}
          onClose={() => setTodoPanel(null)}
          showSite
          canAct={todoAction.canAct}
          onAct={handleTodoAct}
          onOpenTodoPage={schedulerHost?.openTodo ? () => { setTodoPanel(null); schedulerHost.openTodo?.() } : undefined}
        />
      )}

      {viewMode === '일간' && renderDay()}
      {viewMode === '주간' && renderWeek()}
      {viewMode === '주간' && renderWeekDetail()}

      {/* Monthly view: calendar shrinks up, panel slides in from below */}
      {viewMode === '월간' && (
        <div style={{ flex: 1, position: 'relative', overflow: 'hidden' }}>

          {/* Calendar — bottom edge moves up when panel opens, revealing bars-only view */}
          <div
            style={{
              position: 'absolute', top: 0, left: 0, right: 0,
              bottom: daySelected ? '46%' : 0,
              transition: 'bottom .35s cubic-bezier(0.4, 0, 0.2, 1)',
              display: 'flex', flexDirection: 'column',
              overflow: 'hidden',
              padding: '0 12px 4px',
              cursor: daySelected ? 'pointer' : undefined,
            }}
            onClick={daySelected ? () => setDaySelected(false) : undefined}
          >
            <div style={{ flex: 1, minHeight: 0, display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gridTemplateRows: `auto repeat(${weeks.length}, 1fr)`, borderTop: 'var(--border-width-hairline) solid var(--shell-border)', borderLeft: 'var(--border-width-hairline) solid var(--shell-border)', marginTop: 8, overflow: 'hidden' }}>
              {DN.map((d, i) => (
                <div key={d} style={{ textAlign: 'center', fontSize: 10.5, fontWeight: 700, color: i === 0 ? 'var(--status-danger-600)' : i === 6 ? 'var(--status-info-600)' : 'var(--shell-on-surface-2)', padding: '5px 0', borderBottom: 'var(--border-width-hairline) solid var(--shell-border)', borderRight: 'var(--border-width-hairline) solid var(--shell-border)' }}>
                  {d}
                </div>
              ))}
              {weeks.flat().map((day, idx) => {
                const col    = idx % 7
                if (day === null) return (
                  <div key={idx} style={{ background: 'var(--shell-bg)', borderBottom: 'var(--border-width-hairline) solid var(--shell-border)', borderRight: 'var(--border-width-hairline) solid var(--shell-border)' }} />
                )
                const dayTasks = displayTasks.filter(t => day >= t.startDay && day <= t.endDay).sort((a, b) => a.lane - b.lane)
                const dayMiles = filteredMilestones.filter(m => m.milestoneDate.slice(0, 10) === ds(year, month, day))
                const dayOrders     = filteredTasks.filter(t => t.orderDate?.slice(0, 10) === ds(year, month, day))
                const dayDeliveries = filteredTasks.filter(t => t.deliveryDate?.slice(0, 10) === ds(year, month, day))
                const visibleTasks  = dayTasks.filter(t => t.lane < MAX_LANES)
                const overflowCount = dayTasks.length - visibleTasks.length
                const maxLane   = visibleTasks.length ? Math.max(...visibleTasks.map(t => t.lane)) : -1
                const laneSlots = Array.from({ length: maxLane + 1 }, (_, lane) => visibleTasks.find(t => t.lane === lane) ?? null)
                // 마일스톤·발주·입고를 한 줄씩 다 나열하면 칸을 넘쳐 다음 주 칸까지 침범하므로,
                // 합쳐서 최대 MARKER_LIMIT개까지만 보여주고 나머지는 "+N개"로 요약한다(공종 바의
                // overflowCount 표시 방식과 동일한 패턴).
                const dayMarkers: { key: string; color: string; icon: string; label: string }[] = [
                  ...dayMiles.map(m => ({ key: `ms-${m.milestoneId}`, color: siteColorMap.get(m.scheduleId) ?? 'var(--gt-primary)', icon: '◆', label: m.milestoneName })),
                  ...dayOrders.map(t => ({ key: `ord-${t.taskId}`, color: 'var(--gt-primary)', icon: '◆', label: `${t.workTypeName} 발주` })),
                  ...dayDeliveries.map(t => ({ key: `del-${t.taskId}`, color: 'var(--shell-on-surface)', icon: '●', label: `${t.workTypeName} 입고` })),
                ]
                const MARKER_LIMIT = 1
                const visibleMarkers = dayMarkers.slice(0, MARKER_LIMIT)
                const markerOverflow = dayMarkers.length - visibleMarkers.length
                const isToday = year === now.getFullYear() && month === now.getMonth() + 1 && day === now.getDate()
                const isSel   = day === selDay && daySelected
                return (
                  <div
                    key={idx}
                    role="button"
                    tabIndex={0}
                    onClick={(e) => {
                      e.stopPropagation()
                      // 같은 날을 다시 클릭하면 닫고, 다른 날을 클릭하면 달력으로 안 돌아가고
                      // 바로 그 날의 일정으로 전환
                      if (daySelected && day === selDay) { setDaySelected(false) }
                      else { setSelDay(day); setDaySelected(true) }
                    }}
                    onKeyDown={e => {
                      if (e.key !== 'Enter' && e.key !== ' ') return
                      e.preventDefault()
                      e.stopPropagation()
                      if (daySelected && day === selDay) { setDaySelected(false) }
                      else { setSelDay(day); setDaySelected(true) }
                    }}
                    style={{ padding: daySelected ? '1px 0' : '3px 0 2px', borderBottom: 'var(--border-width-hairline) solid var(--shell-border)', borderRight: 'var(--border-width-hairline) solid var(--shell-border)', background: isSel ? 'var(--gt-subtle)' : 'var(--shell-surface)', cursor: 'pointer', transition: 'background .12s', overflow: 'hidden', position: 'relative' }}
                  >
                    <div style={{ height: daySelected ? 13 : 20, display: 'flex', alignItems: 'center', marginBottom: daySelected ? 1 : 2, padding: '0 2px' }}>
                      {isToday
                        ? <div style={{ width: 14, height: 14, borderRadius: '50%', background: 'var(--gt-primary)', color: 'var(--gt-on-primary)', fontSize: 8.5, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{day}</div>
                        : isSel
                          ? <div style={{ width: 14, height: 14, borderRadius: '50%', background: 'var(--gt-subtle)', color: 'var(--gt-primary)', fontSize: 8.5, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{day}</div>
                          : <span style={{ fontSize: 'var(--type-caption-size)', fontWeight: 400, color: (col === 0 || holidays.has(ds(year, month, day))) ? 'var(--status-danger-600)' : col === 6 ? 'var(--status-info-600)' : 'var(--shell-on-surface)', padding: '0 2px' }}>{day}</span>
                      }
                    </div>
                    {laneSlots.map((task, laneIdx) => {
                      const bH = daySelected ? 8 : LANE_H
                      if (!task) return <div key={laneIdx} style={{ height: bH, marginBottom: LANE_GAP }} />
                      const isVisualStart = task.startDay === day || col === 0
                      const isVisualEnd   = task.endDay   === day || col === 6
                      return (
                        <div key={laneIdx} style={{ background: task.color, color: 'var(--gt-on-primary)', fontSize: 10.5, fontWeight: 700, height: bH, lineHeight: `${bH}px`, padding: `0 ${!daySelected && isVisualStart ? 4 : 0}px`, marginBottom: LANE_GAP, borderRadius: `${isVisualStart ? 3 : 0}px ${isVisualEnd ? 3 : 0}px ${isVisualEnd ? 3 : 0}px ${isVisualStart ? 3 : 0}px`, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {!daySelected && isVisualStart ? task.workTypeName : ''}
                        </div>
                      )
                    })}
                    {overflowCount > 0 && <div style={{ position: 'absolute', bottom: 2, left: 4, fontSize: 8, color: 'var(--shell-on-surface-2)' }}>+{overflowCount}</div>}
                    {/* 하루 일정 패널이 열려 좁아진 상태에서만, 그 날 공사 일정이 있으면 마커 숨김.
                        패널이 닫혀 있으면(평소 달력) 최대 MARKER_LIMIT개만 보여주고 나머지는 +N개로 요약 */}
                    {(!daySelected || dayTasks.length === 0) && visibleMarkers.map(mk => (
                      <div key={mk.key} style={{ display: 'flex', alignItems: 'center', gap: 2, padding: '0 3px', lineHeight: '12px', marginBottom: 1 }}>
                        <span style={{ fontSize: 7, color: mk.color, flexShrink: 0 }}>{mk.icon}</span>
                        <span style={{ fontSize: 7.5, fontWeight: 700, color: mk.color, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, minWidth: 0 }}>{mk.label}</span>
                        {markerOverflow > 0 && <span style={{ fontSize: 7, color: 'var(--shell-on-surface-2)', flexShrink: 0, marginLeft: 2 }}>+{markerOverflow}개</span>}
                      </div>
                    ))}
                  </div>
                )
              })}
            </div>
          </div>

          {/* Task panel — translateY slide from below, never covers the calendar */}
          <div style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: '46%',
            transform: daySelected ? 'translateY(0)' : 'translateY(100%)',
            transition: 'transform .35s cubic-bezier(0.4, 0, 0.2, 1)',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            borderTop: '2px solid var(--gt-primary)',
            background: 'var(--shell-surface)',
            boxShadow: 'var(--elevation-2)',
          }}>
            {/* Header */}
            <div style={{ padding: '10px 16px 9px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: 'var(--border-width-hairline) solid var(--shell-border)', flexShrink: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ width: 32, height: 32, borderRadius: 12, background: 'var(--gt-subtle)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <svg aria-hidden="true" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="var(--gt-primary)" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="3" y1="10" x2="21" y2="10"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="16" y1="2" x2="16" y2="6"/>
                  </svg>
                </div>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--shell-on-surface)', letterSpacing: '-.4px' }}>{month}월 {selDay}일 ({selDow})</div>
                  <div style={{ fontSize: 'var(--type-caption-size)', color: 'var(--shell-on-surface-2)', marginTop: 1 }}>{panelTasks.length + selMilestones.length + selOrders.length + selDeliveries.length}개 공사 일정</div>
                </div>
              </div>
              <Button
                variant="secondary"
                size="sm"
                className="!h-auto gap-1 !px-2.5 !py-1 text-[length:var(--type-caption-size)]"
                onClick={(e) => { e.stopPropagation(); setDaySelected(false) }}
              >닫기</Button>
            </div>
            {/* Task cards */}
            <div tabIndex={0} role="region" aria-label={`${month}월 ${selDay}일 일정`} style={{ flex: 1, overflowY: 'auto', padding: '8px 12px 12px', display: 'flex', flexDirection: 'column', gap: 6 }}>
              {panelTasks.length === 0 && selMilestones.length === 0 && selOrders.length === 0 && selDeliveries.length === 0
                ? <div style={{ padding: 16, textAlign: 'center', fontSize: 12, color: 'var(--shell-hint)' }}>등록된 작업이 없습니다</div>
                : <>
                  {selMilestones.map(m => {
                    const color    = siteColorMap.get(m.scheduleId) ?? 'var(--gt-primary)'
                    const siteName = siteNameMap.get(m.scheduleId) ?? ''
                    return (
                      <div key={`ms-${m.milestoneId}`} style={{ display: 'flex', alignItems: 'center', padding: '10px 12px', background: 'var(--gt-subtle)', borderRadius: 12, border: `var(--border-width-hairline) solid color-mix(in srgb, ${color} 20%, transparent)`, borderLeft: `3px solid ${color}` }}>
                        <span style={{ fontSize: 13, color, flexShrink: 0, marginRight: 8 }}>◆</span>
                        {siteName && <span style={{ fontSize: 'var(--type-caption-size)', color: 'var(--shell-on-surface-2)', flexShrink: 0, marginRight: 4 }}>{siteName} /&nbsp;</span>}
                        <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--shell-on-surface)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.milestoneName}</span>
                      </div>
                    )
                  })}
                  {selOrders.map(t => (
                    <div key={`ord-${t.taskId}`} style={{ display: 'flex', alignItems: 'center', padding: '10px 12px', background: 'var(--gt-subtle)', borderRadius: 12, border: 'var(--border-width-hairline) solid color-mix(in srgb, var(--gt-primary) 25%, var(--shell-surface))', borderLeft: '3px solid var(--gt-primary)' }}>
                      <span style={{ fontSize: 13, color: 'var(--gt-primary)', flexShrink: 0, marginRight: 8 }}>◆</span>
                      <span style={{ fontSize: 'var(--type-caption-size)', color: 'var(--shell-on-surface-2)', flexShrink: 0, marginRight: 4 }}>{t.siteName} /&nbsp;</span>
                      <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--shell-on-surface)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.workTypeName} 발주</span>
                    </div>
                  ))}
                  {selDeliveries.map(t => (
                    <div key={`del-${t.taskId}`} style={{ display: 'flex', alignItems: 'center', padding: '10px 12px', background: 'var(--shell-bg)', borderRadius: 12, border: 'var(--border-width-hairline) solid var(--shell-border)', borderLeft: '3px solid var(--shell-on-surface)' }}>
                      <span style={{ fontSize: 13, color: 'var(--shell-on-surface)', flexShrink: 0, marginRight: 8 }}>●</span>
                      <span style={{ fontSize: 'var(--type-caption-size)', color: 'var(--shell-on-surface-2)', flexShrink: 0, marginRight: 4 }}>{t.siteName} /&nbsp;</span>
                      <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--shell-on-surface)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.workTypeName} 입고</span>
                    </div>
                  ))}
                  {panelTasks.map(t => {
                    const color   = siteColorMap.get(t.scheduleId) ?? 'var(--gt-primary)'
                    const start   = new Date(t.taskStartDate.slice(0, 10))
                    const end     = new Date(t.taskEndDate.slice(0, 10))
                    const total   = Math.round((end.getTime() - start.getTime()) / 86400000) + 1
                    const elapsed = Math.round((new Date(year, month - 1, selDay).getTime() - start.getTime()) / 86400000) + 1
                    const stLabel: string = t.taskProgressStatus === 'COMPLETE' ? '완료' : '예정'
                    const stBg  = stLabel === '시공중' ? 'color-mix(in srgb, var(--color-success) 8%, var(--shell-surface))' : stLabel === '완료' ? 'color-mix(in srgb, var(--color-info) 8%, var(--shell-surface))' : 'color-mix(in srgb, var(--color-warning) 10%, var(--shell-surface))'
                    const stC   = stLabel === '시공중' ? 'var(--status-success-700)' : stLabel === '완료' ? 'var(--status-info-700)' : 'var(--status-warning-700)' // 뱃지 텍스트 = 스케일 700 (D-08 §6.5, AA)
                    const sm = t.taskStartDate.slice(5, 7).replace(/^0/, '')
                    const sd = t.taskStartDate.slice(8, 10).replace(/^0/, '')
                    const em = t.taskEndDate.slice(5, 7).replace(/^0/, '')
                    const ed = t.taskEndDate.slice(8, 10).replace(/^0/, '')
                    return (
                      <div key={t.taskId} style={{ display: 'flex', alignItems: 'flex-start', padding: '10px 12px', background: 'var(--shell-surface)', borderRadius: 12, border: 'var(--border-width-hairline) solid var(--shell-border)', borderLeft: `3px solid ${color}` }}>
                        <div style={{ width: 60, flexShrink: 0, marginRight: 8 }}>
                          <div style={{ fontSize: 9.5, fontWeight: 700, color: 'var(--gt-on-primary)', background: color, borderRadius: 4, padding: '2px 4px', textAlign: 'center', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.workTypeName}</div>
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 3 }}>
                            <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--shell-on-surface)', letterSpacing: '-.2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>{t.workTypeName}</span>
                            <span style={{ fontSize: 10, fontWeight: 700, color: stC, background: stBg, borderRadius: 4, padding: '2px 7px', flexShrink: 0, marginLeft: 6 }}>{stLabel}</span>
                          </div>
                          <div style={{ fontSize: 10.5, color: 'var(--shell-on-surface-2)', marginBottom: 2 }}>{sm}/{sd} ~ {em}/{ed} · {total}일</div>
                          <div style={{ fontSize: 10.5, color: 'var(--shell-on-surface-2)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.siteName} · {t.foremanName ?? '미배정'}</span>
                            <span style={{ color, fontWeight: 700, flexShrink: 0, marginLeft: 6 }}>{elapsed}/{total}일차</span>
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </>
              }
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
